import { gql, useLazyQuery, useQuery } from '@apollo/client';

import { NavigationSubsection, Topics } from '@app/context/constants';

import { Locales } from '@components/block/Header/LangSelect';
import { AUDIT_CHAT_FIELDS, AuditChat } from '@dieterApi/audit/useAuditChatQuery';
import { CHAT_FIELDS, Chat } from '@dieterApi/chat/useInitiateChatMutation';
import { CompanySubscription } from '@dieterApi/company/useCompanyQuery';
import { QuestionnaireState } from '@legalosApi/types/QuestionnaireState';
import { useTranslation } from 'react-i18next';

export const EMPLOYEE_FIELDS = gql`
  fragment UserEmployeeFields on Stakeholder {
    id
    name
    email
    createdAt
    archivedAt
  }
`;

// Minimal company fields for user query
export const COMPANY_MINIMAL_FIELDS = gql`
  fragment UserCompanyMinimalFields on Company {
    id
    name
    isPrincipal
  }
`;

export const USER_FIELDS = gql`
  ${COMPANY_MINIMAL_FIELDS}
  ${CHAT_FIELDS}
  ${AUDIT_CHAT_FIELDS}

  fragment UserFields on User {
    id
    createdAt
    firstName
    lastName
    email
    confirmed
    source
    imgUrl
    mailSent
    isAdmin
    role
    cohort
    isAnonymous
    isSubscriber
    isTrialing
    maxTenants
    maxEmployees
    marketingConsent
    conditionsConsent
    navState
    subscription
    hasPassword
    previousLogin
    klausMergeOffer {
      orgs {
        id
        name
      }
    }
    mfaEnabled
    mfaVerified
    experimental
    celloActive
    locale
    company {
      ...UserCompanyMinimalFields
    }
    companies {
      ...UserCompanyMinimalFields
      street
      zip
      city
      phone
      website
      billingActive
      assignedDpoUserId
      klausLink {
        klausOrgId
        klausOrgName
        createdAt
        createdByEmail
      }
      users {
        id
        email
        role
        confirmed
        createdAt
        lastLogin
        mfaEnabled
        dpoName
        dpoEmail
        dpoPhone
        dpoAddress
        dpoCompany
        dpoAvatarUrl
        companies {
          id
          name
        }
      }
    }

    celloToken
    celloProductId
    hubspotIdToken
    chatwootIdentifierHash
    mittwaldUserId
    chats {
      ...ChatFields
    }
    auditChats {
      ...AuditChatFields
    }
  }
`;

export const GET_USER = gql`
  ${USER_FIELDS}
  query GetUser($userId: String) {
    getUser(userId: $userId) {
      ...UserFields
    }
  }
`;

export interface User {
  id: string;
  createdAt: string;
  company: {
    id: string;
    name: string;
    isPrincipal: boolean;
  };
  companies: {
    id: string;
    name: string;
    isPrincipal: boolean;
    street?: string;
    zip?: string;
    city?: string;
    phone?: string;
    website?: string;
    billingActive: boolean;
    assignedDpoUserId?: string;
    /** Verknüpfung mit einer Klaus-Organisation (null = nicht verknüpft). */
    klausLink?: {
      klausOrgId: string;
      klausOrgName?: string | null;
      createdAt: string;
      createdByEmail: string;
    } | null;
    users: {
      id: string;
      email: string;
      role: UserRole;
      confirmed: boolean;
      createdAt: string;
      lastLogin: string;
      mfaEnabled: boolean;
      dpoName?: string;
      dpoEmail?: string;
      dpoPhone?: string;
      dpoAddress?: string;
      dpoCompany?: string;
      dpoAvatarUrl?: string;
      companies: { id: string; name: string }[];
    }[];
  }[];
  firstName?: string;
  lastName?: string;
  email?: string;
  role: UserRole;
  locale: Locales;
  confirmed: boolean;
  source: 'local' | 'linkedin' | 'shopify' | 'wix' | 'mittwald';
  imgUrl?: string;
  mailSent: number;
  maxTenants: number;
  maxEmployees: number;
  cohort: 'A' | 'B' | 'C';
  isAdmin: boolean;
  isAnonymous: boolean;
  isSubscriber: boolean;
  isTrialing: boolean;
  marketingConsent: boolean;
  conditionsConsent: boolean;
  navState: string;
  subscription: CompanySubscription;
  hasPassword: boolean;
  previousLogin: string;
  /** Klaus-Orgs des Users, die noch mit keiner Dieter-Firma verknüpft sind (Merge-Angebot nach dem Login); null = nichts anzubieten. */
  klausMergeOffer?: { orgs: { id: string; name: string }[] } | null;
  experimental: boolean;
  celloActive: boolean;
  mfaEnabled: boolean;
  mfaVerified?: boolean;
  token?: string;
  celloToken: string;
  celloProductId: string;
  mittwaldUserId?: string;
  hubspotIdToken?: string;
  chatwootIdentifierHash?: string;
  chats: Chat[];
  auditChats: AuditChat[];
}

export enum UserRole {
  Admin = 'Admin',
  Editor = 'Editor',
  ReadOnly = 'ReadOnly',
  Dpo = 'Dpo',
}

export type UserApplicationQuestionnaires = UserApplication & {
  questionnaires?: UserQuestionnaire[];
};

export type UserTopicApplications = UserTopic & {
  applications: UserApplication[];
};

export type UserTopicApplicationsQuestionnaires = UserTopic & {
  applications: UserApplicationQuestionnaires[];
};

export type UserApplicationTopic = UserApplication & { topic: UserTopic };

export type UserQuestionnaireApplication = UserQuestionnaire & {
  application: UserApplication;
};

export type UserQuestionnaireApplicationTopic = UserQuestionnaire & {
  application: UserApplicationTopic;
};

export interface Coupon {
  code: string;
  createdAt: string;
}

export type ProductFeature =
  | 'MultiTenancy'
  | 'eSignatures'
  | 'Usercentrics'
  | 'Whitelabel'
  | 'WhitelabelAdmin'
  | 'DpoManaged';

export interface Employee {
  id: string;
  createdAt: string;
  name: string;
  email?: string | null;
  archivedAt?: string | null;
}

export interface UserFile {
  id: string;
  name: string;
  size: number;
  uri: string;
  url?: string;
}

export enum ApplicationStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  EXPERIMENTAL = 'EXPERIMENTAL',
}

export enum ApplicationType {
  Primary = 'Primary',
  Secondary = 'Secondary',
  ListEntryLong = 'ListEntryLong',
  ListEntryShort = 'ListEntryShort',
  ListContract = 'ListContract',
}

export enum ApplicationClass {
  Training = 'Training',
  Document = 'Document',
  Contract = 'Contract',
  Analysis = 'Analysis',
}

export interface UserTopic {
  id: Topics;
  title: Topics;
  titleLocales: { de: string; en: string };
  order: number;
  helpText: string[];
  subItemLocales: { id: NavigationSubsection; title: string }[];
  enabled: boolean;
  tags: Tag[];
  files: UserFile[];
  hasFiles: boolean;
  shouldHaveFiles: boolean;
  status: ApplicationStatus;
}

export interface UserApplication {
  id: ApplicationIDs;
  title: ApplicationIDs;
  order: number;
  sectionLabels: string[];
  documentLabels: string[];
  versions: ApplicationVersion[];
  splitApplication: boolean;
  splitDocument: boolean;
  status: ApplicationStatus;
  type: ApplicationType;
  class: ApplicationClass;
  tags?: Tag[];
}

export type ApplicationIDs =
  | 'app-checkup'
  | 'app-ds-mgmt'
  | 'app-dsb-training'
  | 'app-dsv-training'
  | 'app-dsv-incident'
  | 'app-av-training'
  | 'app-av-contract-partners'
  | 'app-av-contract-av'
  | 'app-av-contract-scc'
  | 'app-av-contract-jc'
  | 'app-toms-training'
  | 'app-toms-create'
  | 'app-toms-authorization-concept-create'
  | 'app-dse-training'
  | 'app-dse-privacy-policy-create'
  | 'app-dse-imprint-create'
  | 'app-social-instagram'
  | 'app-social-facebook'
  | 'app-social-linkedin'
  | 'app-social-tiktok'
  | 'app-info-training'
  | 'app-info-create'
  | 'app-info-create-new'
  | 'app-info-consent-create'
  | 'app-video-training'
  | 'app-video-documentation-create'
  | 'app-rights-training'
  | 'app-rights-request-create'
  | 'app-rights-request-right-to-rectification'
  | 'app-rights-request-right-to-restriction'
  | 'app-rights-request-right-to-data-portability'
  | 'app-rights-request-right-to-objection'
  | 'app-rights-request-right-to-revocation'
  | 'app-rights-request-right-to-information'
  | 'app-rights-request-right-to-deletion'
  | 'app-rights-request-right-to-denial-automated-decision'
  | 'app-rights-request-extension-deadline'
  | 'app-employees-training'
  | 'app-employees-commitment-create'
  | 'app-employees-information-create'
  | 'app-employees-applicant-information-create'
  | 'app-employees-basic-training'
  | 'app-vvz-training'
  | 'app-vvz-create'
  | 'app-deletions-concept-create'
  | 'app-dsfa-training'
  | 'app-dsfa-create'
  | 'app-experimental-toms'
  | 'app-meta';

export interface ApplicationVersion {
  version: number;
  versionDate: string;
}

export interface Tag {
  tag: TagValues;
}

export type TagValues =
  | 'noresume'
  | 'nofiles'
  | 'analysis'
  | 'dummy'
  | 'internal'
  | 'external'
  | 'experimental'
  | 'action'
  | 'meta'
  | 'public'
  | 'AVV'
  | 'SCC'
  | 'JC'
  | 'docx'
  | 'pdf'
  | 'html'
  | 'legacy'
  | 'consentlink'
  | 'nosubitems'
  | 'experimental-invisible'
  | 'allopen'
  | 'EXTEND_DEADLINE'
  | 'en'
  | 'de';

export interface UserQuestionnaire {
  id: string;
  questionnaireId: string;
  companyId: string;
  createdAt: string;
  updatedAt: string;
  sectionModifiedAt: string[];
  applicationVersion?: ApplicationVersion;
  state: QuestionnaireState;
  isBroken: boolean;
  sectionStates: QuestionnaireState[];
  progress: number;
  sectionProgress: number[];
  documentConfirmations: DocumentConfirmation[];
  userValueIndex?: string;
  accessToken?: string;
  isNew?: boolean[]; // this is added after backend response by the frontend (NavigationContext)
}

export interface DocumentConfirmation {
  name: string;
  employeeId: string;
  createdAt: string;
  documentNumber: number;
  isStale: boolean;
  staleReason?: string | null;
}

export interface GetUserResult {
  getUser: User;
}

export interface GetUserInput {
  userId?: string;
}

export function useUserQuery() {
  const { i18n } = useTranslation();
  return useQuery<GetUserResult>(GET_USER, {
    onCompleted(data) {
      if (!data?.getUser) return;
      i18n.changeLanguage(data.getUser.locale);
    },
  });
}

export function useUserLazyQuery() {
  return useLazyQuery<GetUserResult, GetUserInput>(GET_USER, {
    fetchPolicy: 'no-cache',
  });
}
